import {
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { getUserData, updateUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import {
  getLanguages,
  getProfile,
  saveProfile,
} from "framework/company";
import {
  ILanguage,
  IProfileData,
  IUserColors,
  IUserSMS,
  IUserSMTP,
} from "models/company";

import { ELanguage } from "models/app";
import { LocalStorage } from "enums/localStorage";
import { UserRoleName } from "types/auth";
import EmptyContent from "components/EmptyContent";
import MuiButton from "components/UI/MuiButton";
import { IAcquireSources } from "models/employeeCompetencies";
import { ICompetencyByRole } from "models/competency";
import { getCompetenciesByRole } from "framework/competencies";
import { getCompetenciesAcquireSources } from "framework/employeeCompetencies";
import EditCompetencyScore from "./partials/CompetencyScore";

const EditAdminSMS = React.lazy(() => import("./partials/SMS/EditSms"));
const EditAdminSMTP = React.lazy(() => import("./partials/SMTP/EditSmtp"));
const EditAdminColors = React.lazy(
  () => import("./partials/Colors/EditColors")
);
const Info = React.lazy(() => import("./partials/Info/Info"));

interface Props {
  feature: Feature;
}

type Entry = {
  competencyId: string;
  scoreId: number;
};

const Profile: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [inputObj, setInputObj] = useState<any>({});
  const [smsSettings, setSmsSettings] = useState<IUserSMS>({} as IUserSMS);
  const [smtpSettings, setSmtpSettings] = useState<IUserSMTP>({} as IUserSMTP);
  const [colorsSettings, setColorsSettings] = useState<IUserColors>(
    {} as IUserColors
  );
  const [countries, setCountries] = useState<
    {
      id: string;
      code: string;
      name: string;
      nameAr: string;
    }[]
  >([]);

  const [gender, setGender] = useState<
    {
      id: number;
      name: string;
      nameAr: string;
    }[]
  >([]);

  const [industries, setIndustries] = useState<
    {
      id: string;
      code: string;
      name: string;
      nameAr: string;
    }[]
  >([]);

  const [competencies, setCompetencies] = useState<ICompetencyByRole[]>(
    [] as ICompetencyByRole[]
  );
  const [acquireSources, setAcquireSources] = useState<IAcquireSources[]>(
    [] as IAcquireSources[]
  );

  const [languages, setLanguage] = useState<ILanguage[]>([] as ILanguage[]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [profile, setProfile] = useState<IProfileData>({} as IProfileData);
  const userData = getUserData();
  const { user, roleName, colors } = userData;

  const { t, i18n } = useTranslation();

  const handleChange = (name: string, value: any) => {
    setInputObj((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };
  const { setAlert } = useAlert();

  const saveProfileMutation = useMutation({
    mutationFn: (data: FormData) => {
      return saveProfile(data);
    },
  });

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const [profileData, languagesData, competenciesData, acquireSourcesData] =
        await Promise.all([
          queryClient.fetchQuery("get Profile", () => getProfile()),
          queryClient.fetchQuery("get Languages", () => getLanguages()),
          roleName === UserRoleName.EMPLOYEE
            ? queryClient.fetchQuery("get Competencies By Role", () =>
              getCompetenciesByRole()
            )
            : () => [],
          queryClient.fetchQuery("get Acquire Sources", () =>
            getCompetenciesAcquireSources()
          ),
        ]);
      const { profile, countries, industries, gender } = profileData;
      setCountries(countries);
      setGender(gender.result);
      setIndustries(industries);
      if (profile.competenies) {
        setEntries(
          profile.competenies.map((entry) => {
            return {
              competencyId: entry.competencyId,
              scoreId: entry.acquireSource,
            };
          })
        );
      }
      const smsSettingsData: IUserSMS = {
        twilioAccountSid: profile.twilioAccountSid,
        twilioAuthToken: profile.twilioAuthToken,
        twilioFromSenderName: profile.twilioFromSenderName,
        twilioWhatsappFromMobile: profile.twilioWhatsappFromMobile,
      };
      const smtpSettingsData: IUserSMTP = {
        smtpFromEmail: profile.smtpFromEmail,
        smtpHost: profile.smtpHost,
        smtpPassword: profile.smtpPassword,
        smtpPort: profile.smtpPort,
        smtpUsername: profile.smtpUsername,
      };
      const smsColorsData: IUserColors = {
        addUpdateIconColor: profile.addUpdateIconColor,
        addUpdateIconHoverColor: profile.addUpdateIconHoverColor,
        deleteIconColor: profile.deleteIconColor,
        deleteIconHoverColor: profile.deleteIconHoverColor,
        viewIconColor: profile.viewIconColor,
        viewIconHoverColor: profile.viewIconHoverColor,
        cancelButtonColor: profile.cancelButtonColor,
        cancelButtonHoverColor: profile.cancelButtonHoverColor,
        chartMainColor: profile.chartMainColor,
        menuItemSelectedColor: profile.menuItemSelectedColor,
        menuBgColor: profile.menuBgColor,
        menuItemHoverColor: profile.menuItemHoverColor,
        labelColor: profile.labelColor,
      };
      setSmsSettings(smsSettingsData);
      setSmtpSettings(smtpSettingsData);
      setColorsSettings(smsColorsData);
      setProfile(profile);
      setLanguage(languagesData);
      setCompetencies(competenciesData);
      setAcquireSources(acquireSourcesData);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleSaveProfile = async () => {
    const data: IProfileData = inputObj;
    try {
      setLoading2(true);
      const createInput = new FormData();
      if (data.languageCode) createInput.append("LanguageCode", data.languageCode.toString());
      if (data.name) createInput.append("name", data.name);
      if (data.nameAr) createInput.append("nameAr", data.nameAr);
      if (data.userName) createInput.append("UserName", data.userName);
      if (data.firstName) createInput.append("FirstName", data.firstName);
      if (data.firstNameAr) createInput.append("FirstNameAr", data.firstNameAr);
      if (data.middleName) createInput.append("MiddleName", data.middleName);
      if (data.middleNameAr)
        createInput.append("MiddleNameAr", data.middleNameAr);
      if (data.lastName) createInput.append("LastName", data.lastName);
      if (data.lastNameAr) createInput.append("LastNameAr", data.lastNameAr);
      if (data.biography) createInput.append("Biography", data.biography);
      if (data.biographyAr) createInput.append("BiographyAr", data.biographyAr);
      if (data.email) createInput.append("Email", data.email);
      if (entries.length > 0)
        createInput.append(
          "Competencies",
          JSON.stringify(
            entries.map((item) => {
              return {
                CompetencyId: item.competencyId,
                AcquireSource: item.scoreId.toString(),
              };
            })
          )
        );
      if (data.acquisitionSource)
        createInput.append("AcquisitionSource", data.acquisitionSource);
      if (data.gender) createInput.append("Gender", data.gender.toString());
      if (data.withdrawalDeadline) createInput.append("WithdrawalDeadline", data.withdrawalDeadline.toString());
      if (data.registrationNumber)
        createInput.append(
          "RegistrationNumber",
          data.registrationNumber.toString()
        );
      if (data.residenceCountryId)
        createInput.append("ResidenceCountryId", data.residenceCountryId);
      if (data.dob) {
        const dateOnly = new Date(data.dob).toISOString().split("T")[0];
        createInput.append("Dob", dateOnly);
      }
      if (data.logo) createInput.append("Logo", data?.logo[0] || data?.logo);
      if (data.profilePicture)
        createInput.append(
          "ProfilePicture",
          data?.profilePicture[0] || data?.profilePicture
        );

      if (data.chartMainColor)
        createInput.append("chartMainColor", data.chartMainColor);
      if (data.cancelButtonColor)
        createInput.append("cancelButtonColor", data.cancelButtonColor);
      if (data.cancelButtonHoverColor)
        createInput.append(
          "cancelButtonHoverColor",
          data.cancelButtonHoverColor
        );
      if (data.deleteIconColor)
        createInput.append("deleteIconColor", data.deleteIconColor);
      if (data.deleteIconHoverColor)
        createInput.append("deleteIconHoverColor", data.deleteIconHoverColor);
      if (data.menuBgColor) createInput.append("menuBgColor", data.menuBgColor);
      if (data.menuItemHoverColor)
        createInput.append("menuItemHoverColor", data.menuItemHoverColor);
      if (data.menuItemSelectedColor)
        createInput.append("menuItemSelectedColor", data.menuItemSelectedColor);
      if (data.addUpdateIconColor)
        createInput.append("addUpdateIconColor", data.addUpdateIconColor);
      if (data.addUpdateIconHoverColor)
        createInput.append(
          "addUpdateIconHoverColor",
          data.addUpdateIconHoverColor
        );
      if (data.viewIconColor)
        createInput.append("viewIconColor", data.viewIconColor);
      if (data.viewIconHoverColor)
        createInput.append("viewIconHoverColor", data.viewIconHoverColor);

      // SMTP configuration
      if (data.smtpPort)
        createInput.append("smtpPort", data.smtpPort.toString().trim());
      if (data.smtpFromEmail)
        createInput.append("smtpFromEmail", data.smtpFromEmail);
      if (data.smtpPassword)
        createInput.append("smtpPassword", data.smtpPassword);
      if (data.smtpUsername)
        createInput.append("smtpUsername", data.smtpUsername);
      if (data.smtpHost) createInput.append("smtpHost", data.smtpHost);

      // Twilio configuration
      if (data.twilioAccountSid)
        createInput.append("twilioAccountSid", data.twilioAccountSid);
      if (data.twilioAuthToken)
        createInput.append("twilioAuthToken", data.twilioAuthToken);
      if (data.twilioFromSenderName)
        createInput.append("twilioFromSenderName", data.twilioFromSenderName);
      if (data.twilioWhatsappFromMobile)
        createInput.append(
          "twilioWhatsappFromMobile",
          data.twilioWhatsappFromMobile
        );

      if (data.labelColor) createInput.append("labelColor", data.labelColor);

      if (data.industryId) createInput.append("industryId", data.industryId);
      if (data.countryId) createInput.append("CountryId", data.countryId);

      await saveProfileMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "Profile updated successfully",
        type: "success",
      });

      updateUserData(LocalStorage.COLORS, {
        ...colors,
        ...Object.keys(data).reduce((acc, key) => {
          const typedKey = key as keyof typeof colors;

          if (data[typedKey] !== undefined && data[typedKey] !== null) {
            acc[typedKey] = data[typedKey];
          }
          return acc;
        }, {} as Partial<typeof colors>),
      });
      setInputObj({});
      fetchData();
      if (data.languageCode) {
        i18n.changeLanguage(
          +data.languageCode === 1 ? ELanguage.ARABIC : ELanguage.ENGLISH
        );
        updateUserData(LocalStorage.USER, { ...user, languageCode: +data.languageCode });
      }
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setInputObj({});
      setLoading2(false);
    }
  };

  if (!Object.values(UserRoleName).includes(roleName as UserRoleName))
    return (
      <EmptyContent
        icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
        header={t("T_no_permissions")}
        message={t("T_no_permissions_message")}
      />
    );

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography variant="h3" color={"header.primary"}>
            {t(feature.name)}
          </Typography>
        </Grid>
        <Info
          role={roleName}
          profile={profile}
          onChanges={handleChange}
          languages={languages}
          countries={countries}
          gender={gender}
          industries={industries}
          loading={loading2}
        />

        {roleName === UserRoleName.EMPLOYEE && (
          <EditCompetencyScore
            competencies={competencies}
            scores={acquireSources}
            setEntries={setEntries}
            entries={entries}
          />
        )}

        {(roleName === UserRoleName.COMPANY_ADMIN ||
          roleName === UserRoleName.VENDOR_ADMIN) && (
            <EditAdminSMS sms={smsSettings} onChanges={handleChange} />
          )}

        {(roleName === UserRoleName.COMPANY_ADMIN ||
          roleName === UserRoleName.VENDOR_ADMIN) && (
            <EditAdminSMTP
              smtp={smtpSettings}
              onSubmit={handleSaveProfile}
              onChanges={handleChange}
            />
          )}

        {roleName === UserRoleName.COMPANY_ADMIN && (
          <EditAdminColors
            colors={colorsSettings}
            onSubmit={handleSaveProfile}
            onChanges={handleChange}
          />
        )}
      </Stack>
      <MuiButton
        variant="contained"
        sx={{ bgcolor: "primary.main", color: "white", marginBlock: "10px" }}
        size="large"
        onClick={handleSaveProfile}
        disabled={loading2}
      >
        {t("T_save")}
        <CircularProgress
          size={10}
          sx={{ mx: 2, display: loading2 ? "" : "none" }}
        />
      </MuiButton>
    </>
  );
};
//|| Object.keys(inputObj).length === 0
export default Profile;
